import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { AUTH_EXPIRED_EVENT } from '../utils/apiAuth';

// Set when the session ends because the API rejected the token; the login page explains it.
export const SESSION_EXPIRED_FLAG = 'sessionExpired';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasLineVotesAccess, setHasLineVotesAccess] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  // The API rejected the stored token (expired after 24 hours, or revoked): sign out locally
  // so protected pages send the user to the login page instead of showing empty data.
  useEffect(() => {
    const onExpired = () => {
      if (!localStorage.getItem('authToken')) return;
      localStorage.removeItem('authToken');
      localStorage.removeItem('userSession');
      try {
        sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
      } catch (e) {
        // the login page just won't show the explanation
      }
      setUser(null);
      setIsAuthenticated(false);
      setHasLineVotesAccess(false);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const checkAuthStatus = async () => {
    try {
      const userData = await authService.checkSession();
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
        
        // Check line votes access
        const lineVotesAccess = await authService.checkLineVotesAccess();
        setHasLineVotesAccess(lineVotesAccess);
      }
    } catch (error) {
      // auth check failed
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const userData = await authService.login(email, password);
      setUser(userData);
      setIsAuthenticated(true);
      
      // Check line votes access
      const lineVotesAccess = await authService.checkLineVotesAccess();
      setHasLineVotesAccess(lineVotesAccess);
      
      return { success: true };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const register = async (firstName, lastName, email, password) => {
    try {
      await authService.register(firstName, lastName, email, password);
      return { success: true };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
      setHasLineVotesAccess(false);
    } catch (error) {
      // logout failed silently
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    checkAuthStatus,
    hasLineVotesAccess
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
